import type {
  PanelAuditFilters,
  PanelAuditResponse,
  PanelPlatformMembersResponse,
  PanelPlatformAuditResponse,
  PanelPlatformOverviewResponse,
  PanelChannelOverview,
  PanelChannelLocation,
  PanelChannelControls,
  PanelChannelsResponse,
  PanelEventsResponse,
  PanelEventChangesResponse,
  PanelEventFilters,
  PanelMember,
  PanelMembersResponse,
  PanelModeratorStatus,
  PanelModuleState,
  PanelTemplateWarningResponse,
  PanelModulesResponse,
  PanelSystemResponse,
  PanelTwitchUser,
} from "../panel-contract";
import type { ChannelControlDuration, ChannelRole, ChatOutputTarget, OverlayElementKind } from "../contracts/values";
import type { ModuleRegisteredTemplateVariable } from "../modules/contract";
import type { DryRunRequest, DryRunResponse } from "../contracts/dry-run";
import type { StreamMarkerResult, TestMessageResult } from "../contracts/quick-actions";
import type { NormalizedWeather } from "../modules/weather/contracts";
import type { SunDay, SunAltitudeInterval } from "../modules/sun/domain";

import { PanelApiError } from "../contracts/panel-error";

export { PanelApiError };


const hasParentPathSegment = (input: string): boolean => {
  const pathEnd = input.search(/[?#]/);
  const path = pathEnd === -1 ? input : input.slice(0, pathEnd);
  return path.split("/").some((segment) => {
    try {
      return decodeURIComponent(segment) === "..";
    } catch {
      return false;
    }
  });
};

const isAllowedRequestPath = (pathname: string): boolean =>
  pathname === "/api/channels" ||
  pathname.startsWith("/api/channels/") ||
  pathname === "/api/platform" ||
  pathname.startsWith("/api/platform/") ||
  pathname === "/api/csrf" ||
  pathname === "/auth/logout";

const resolveRequestUrl = (input: string): URL => {
  let url: URL;
  try {
    url = new URL(input, window.location.origin);
  } catch {
    throw new PanelApiError(400, "panel_request_not_allowed");
  }
  if (url.origin !== window.location.origin || hasParentPathSegment(input) || !isAllowedRequestPath(url.pathname)) {
    throw new PanelApiError(400, "panel_request_not_allowed");
  }
  return url;
};

export const requestJson = async <T>(input: string, init?: RequestInit): Promise<T> => {
  const url = resolveRequestUrl(input);
  const response = await fetch(url, { ...init, credentials: "same-origin" });
  if (!response.ok) {
    const responseText = await response.text();
    let code: string | null = null;
    let details: unknown = null;
    try {
      const parsed: unknown = JSON.parse(responseText);
      if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
        details = parsed;
        const error = (parsed as Record<string, unknown>).error;
        if (typeof error === "string" && error.length > 0) code = error;
      }
    } catch {
      // Error responses may also be plain text (old worker, proxy, network failure).
    }
    throw new PanelApiError(response.status, code, details);
  }
  if (response.status === 204) return undefined as T;
  return response.json();
};

export const dryRunChatMessage = (
  channelId: string,
  request: DryRunRequest,
  signal?: AbortSignal,
): Promise<DryRunResponse> => requestMutation<DryRunResponse>(channelPath(channelId, "dry-run"), "POST", request, signal);

const requestOptions = (signal: AbortSignal | undefined): RequestInit | undefined =>
  signal === undefined ? undefined : { signal };

const channelPath = (channelId: string, suffix: string): string =>
  `/api/channels/${encodeURIComponent(channelId)}/${suffix}`;

export interface PanelOverlayToken {
  id: string;
  name: string | null;
  createdAt: string;
  createdBy: string | null;
  lastUsedAt: string | null;
  expiresAt: string | null;
}

export interface PanelOverlayTokensResponse {
  tokens: readonly PanelOverlayToken[];
  nextOffset: number | null;
}

export interface PanelOverlayElement {
  id: string;
  kind: OverlayElementKind;
  label: string;
  variableName: string | null;
  missingVariableName?: string | null;
  text: string;
  config: Readonly<Record<string, unknown>>;
  x: number;
  y: number;
  scalePercent: number;
  z: number;
  inComposition: boolean;
}

export interface PanelOverlay {
  id: string;
  channelId: string;
  name: string;
  width: number;
  height: number;
  css: string;
  revision: number;
  createdAt: string;
  updatedAt: string;
  elements: readonly PanelOverlayElement[];
}

export interface PanelOverlaySummary {
  id: string;
  name: string;
  width: number;
  height: number;
  revision: number;
  elementCount: number;
  accessCount: number;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PanelOverlayAccess {
  tokenId: string;
  overlayId: string;
  label: string;
  createdAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  lastUsedAt: string | null;
  recoverable: boolean;
}

const overlayTokensPath = (channelId: string, tokenId?: string): string =>
  `${channelPath(channelId, "overlay-tokens")}${tokenId === undefined ? "" : `/${encodeURIComponent(tokenId)}/revoke`}`;

export const fetchOverlayTokens = (channelId: string, offset = 0, signal?: AbortSignal): Promise<PanelOverlayTokensResponse> =>
  requestJson<PanelOverlayTokensResponse>(
    `${overlayTokensPath(channelId)}${offset === 0 ? "" : `?offset=${String(offset)}`}`,
    requestOptions(signal),
  );

export const revokeOverlayToken = async (
  channelId: string,
  tokenId: string,
  reason: string,
): Promise<{ closingPending: boolean }> => {
  const result = await requestMutation<{ closingPending: boolean } | undefined>(
    overlayTokensPath(channelId, tokenId),
    "POST",
    { reason },
  );
  return result ?? { closingPending: false };
};

const overlaysPath = (channelId: string, overlayId?: string): string =>
  `${channelPath(channelId, "overlays")}${overlayId === undefined ? "" : `/${encodeURIComponent(overlayId)}`}`;

const overlayAccessesPath = (channelId: string, overlayId: string, tokenId?: string, action?: "reveal" | "replace" | "revoke"): string => {
  const base = `${overlaysPath(channelId, overlayId)}/accesses`;
  if (tokenId === undefined) return base;
  const accessPath = `${base}/${encodeURIComponent(tokenId)}`;
  return action === undefined ? accessPath : `${accessPath}/${action}`;
};

export const fetchOverlays = (channelId: string, signal?: AbortSignal): Promise<{ overlays: readonly PanelOverlaySummary[]; maximum: number; elementMaximum: number }> =>
  requestJson(overlaysPath(channelId), signal === undefined ? {} : { signal });

export const createOverlay = (channelId: string, input: {
  name: string;
  width: number;
  height: number;
  elements: readonly Omit<PanelOverlayElement, "missingVariableName">[];
}): Promise<{ overlay: PanelOverlay }> =>
  requestMutation(overlaysPath(channelId), "POST", input);

export const importLegacyOverlay = (channelId: string, input: {
  token: string;
  variableName: string;
  text: string;
}): Promise<{ overlay: PanelOverlay; closingPending?: boolean }> =>
  requestMutation(`${overlaysPath(channelId)}/import-legacy`, "POST", input);

export const fetchOverlay = (channelId: string, overlayId: string, signal?: AbortSignal): Promise<{ overlay: PanelOverlay }> =>
  requestJson(overlaysPath(channelId, overlayId), signal === undefined ? {} : { signal });

export type PanelOverlayDraft = Pick<PanelOverlay, "name" | "width" | "height" | "css" | "elements">;
export interface PanelOverlayReconnectExpectation {
  elementId: string;
  missingVariableName: string;
}

export const saveOverlay = (
  channelId: string,
  overlayId: string,
  baseRevision: number,
  draft: PanelOverlayDraft,
  reconnectExpectation?: PanelOverlayReconnectExpectation,
): Promise<{ overlay: PanelOverlay }> => requestMutation(overlaysPath(channelId, overlayId), "PUT", {
  baseRevision,
  ...draft,
  ...(reconnectExpectation === undefined ? {} : { reconnectExpectation }),
});

export const deleteOverlay = (channelId: string, overlayId: string, baseRevision: number): Promise<{ closingPending?: boolean } | undefined> =>
  requestMutation(overlaysPath(channelId, overlayId), "DELETE", { baseRevision });

export const fetchOverlayAccesses = (channelId: string, overlayId: string, signal?: AbortSignal): Promise<{
  accesses: readonly PanelOverlayAccess[];
  activeCount: number;
  maximum: number;
}> => requestJson(overlayAccessesPath(channelId, overlayId), requestOptions(signal));

export interface PanelIssuedOverlayAccess {
  tokenId: string;
  overlayUrl: string;
  label: string;
  expiresAt: string | null;
  replacesTokenId?: string;
  closingPending?: boolean;
}

export const issueOverlayAccess = (channelId: string, overlayId: string, label: string): Promise<PanelIssuedOverlayAccess> =>
  requestMutation(overlayAccessesPath(channelId, overlayId), "POST", { label });

export const revealOverlayAccess = (channelId: string, overlayId: string, tokenId: string): Promise<{ overlayUrl: string }> =>
  requestMutation(overlayAccessesPath(channelId, overlayId, tokenId, "reveal"), "POST", {});

export const replaceOverlayAccess = (channelId: string, overlayId: string, tokenId: string): Promise<PanelIssuedOverlayAccess> =>
  requestMutation(overlayAccessesPath(channelId, overlayId, tokenId, "replace"), "POST", {});

export const revokeOverlayAccess = async (channelId: string, overlayId: string, tokenId: string): Promise<{ closingPending: boolean }> => {
  const result = await requestMutation<{ closingPending?: boolean } | undefined>(
    overlayAccessesPath(channelId, overlayId, tokenId, "revoke"), "POST", {},
  );
  return { closingPending: result?.closingPending ?? false };
};

export const removeOverlayAccess = (channelId: string, overlayId: string, tokenId: string): Promise<void> =>
  requestMutation(overlayAccessesPath(channelId, overlayId, tokenId), "DELETE");

const memberPath = (channelId: string, userId?: string): string =>
  `${channelPath(channelId, "members")}${userId === undefined ? "" : `/${encodeURIComponent(userId)}`}`;

const modulePath = (channelId: string, moduleId?: string): string =>
  `${channelPath(channelId, "modules")}${moduleId === undefined ? "" : `/${encodeURIComponent(moduleId)}`}`;

export const fetchChannels = (signal?: AbortSignal): Promise<PanelChannelsResponse> =>
  requestJson<PanelChannelsResponse>("/api/channels", requestOptions(signal));

export const getPlatformOverview = (): Promise<PanelPlatformOverviewResponse> =>
  requestJson<PanelPlatformOverviewResponse>("/api/platform");

export const searchPlatformUser = (login: string): Promise<{ user: PanelTwitchUser }> =>
  requestJson<{ user: PanelTwitchUser }>(`/api/platform/users?${new URLSearchParams({ login }).toString()}`);

export const releasePlatformChannel = (
  login: string,
  fullConsent: boolean,
): Promise<{ channel: PanelPlatformOverviewResponse["channels"][number] }> => requestMutation(
  "/api/platform/channels",
  "POST",
  { login, fullConsent: fullConsent },
);

export const setPlatformFullConsent = (
  channelId: string,
  fullConsent: boolean,
): Promise<{ channel: PanelPlatformOverviewResponse["channels"][number] }> => requestMutation(
  `/api/platform/channels/${encodeURIComponent(channelId)}`,
  "PATCH",
  { fullConsent: fullConsent },
);

export const getPlatformMembers = (
  channelId: string,
  cursor: string | null = null,
): Promise<PanelPlatformMembersResponse> => {
  const parameter = new URLSearchParams();
  if (cursor !== null) parameter.set("cursor", cursor);
  const query = parameter.toString();
  return requestJson<PanelPlatformMembersResponse>(
    `/api/platform/channels/${encodeURIComponent(channelId)}/members${query.length > 0 ? `?${query}` : ""}`,
  );
};

export const addPlatformMember = (
  channelId: string,
  userId: string,
  role: "manager" | "operator",
): Promise<{ member: PanelPlatformMembersResponse["members"][number] }> => requestMutation(
  `/api/platform/channels/${encodeURIComponent(channelId)}/members`,
  "POST",
  { userId, role },
);

export const changePlatformMember = (
  channelId: string,
  userId: string,
  role: "manager" | "operator",
): Promise<{ member: PanelPlatformMembersResponse["members"][number] }> => requestMutation(
  `/api/platform/channels/${encodeURIComponent(channelId)}/members/${encodeURIComponent(userId)}`,
  "PATCH",
  { role },
);

export const removePlatformMember = (channelId: string, userId: string): Promise<undefined> =>
  requestMutation<undefined>(
    `/api/platform/channels/${encodeURIComponent(channelId)}/members/${encodeURIComponent(userId)}`,
    "DELETE",
  );

export const getPlatformAudit = (cursor: string | null = null): Promise<PanelPlatformAuditResponse> => {
  const parameter = new URLSearchParams();
  if (cursor !== null) parameter.set("cursor", cursor);
  const query = parameter.toString();
  return requestJson<PanelPlatformAuditResponse>(`/api/platform/audit${query.length > 0 ? `?${query}` : ""}`);
};

export const fetchChannelOverview = (
  channelId: string,
  signal?: AbortSignal,
): Promise<PanelChannelOverview> => requestJson<PanelChannelOverview>(
  channelPath(channelId, "overview"),
  requestOptions(signal),
);

export interface PanelChannelSettings {
  timeZone: string;
  revision: number;
  location: PanelChannelLocation | null;
  locationRevision: number;
  defaultChatTarget: ChatOutputTarget;
  defaultChatTargetRevision: number;
}

export interface PanelBroadcastGame {
  id: string;
  name: string;
  boxArtUrlTemplate?: string;
}

export interface PanelChannelBroadcast {
  title: string;
  game: PanelBroadcastGame | null;
  manageScopeAvailable: boolean;
}

export interface PanelStreamMarkerAvailability {
  manageScopeAvailable: boolean;
}

export const fetchChannelBroadcast = (channelId: string, signal?: AbortSignal): Promise<PanelChannelBroadcast> =>
  requestJson<PanelChannelBroadcast>(channelPath(channelId, "broadcast"), requestOptions(signal));

export const fetchStreamMarkerAvailability = (channelId: string, signal?: AbortSignal): Promise<PanelStreamMarkerAvailability> =>
  requestJson<PanelStreamMarkerAvailability>(channelPath(channelId, "broadcast/markers/availability"), requestOptions(signal));

export const saveChannelBroadcast = (
  channelId: string,
  update: { title?: string; gameId?: string },
): Promise<undefined> => requestMutation<undefined>(channelPath(channelId, "broadcast"), "PATCH", update);

export const createStreamMarker = (channelId: string): Promise<StreamMarkerResult> =>
  requestMutation<StreamMarkerResult>(channelPath(channelId, "broadcast/markers"), "POST");

export const searchChannelGames = async (channelId: string, query: string): Promise<readonly PanelBroadcastGame[]> => {
  const search = new URLSearchParams({ q: query });
  return requestJson<{ games: PanelBroadcastGame[] }>(`${channelPath(channelId, "games")}?${search}`).then(({ games }) => games);
};

export const lookupChannelGames = async (channelId: string, ids: readonly string[]): Promise<readonly PanelBroadcastGame[]> => {
  if (ids.length === 0) return [];
  const search = new URLSearchParams({ ids: ids.join(",") });
  return requestJson<{ games: PanelBroadcastGame[] }>(`${channelPath(channelId, "games")}?${search}`).then(({ games }) => games);
};

export type { PanelChannelLocation } from "../panel-contract";

export interface PanelChannelLocationResult extends PanelChannelLocation {
  country: string;
  admin1: string | null;
}

export const fetchChannelSettings = (channelId: string, signal?: AbortSignal): Promise<PanelChannelSettings> =>
  requestJson<PanelChannelSettings>(channelPath(channelId, "settings"), requestOptions(signal));

export const saveChannelTimeZone = (
  channelId: string,
  revision: number,
  timeZone: string,
): Promise<{ ok: boolean; timeZone: string; revision: number }> => requestMutation(
  channelPath(channelId, "settings"),
  "PATCH",
  { revision, timeZone },
);

export const saveChannelDefaultChatTarget = (
  channelId: string,
  revision: number,
  defaultChatTarget: ChatOutputTarget,
): Promise<{ ok: boolean; defaultChatTarget: ChatOutputTarget; defaultChatTargetRevision: number }> => requestMutation(
  channelPath(channelId, "settings"),
  "PATCH",
  { defaultChatTargetRevision: revision, defaultChatTarget },
);

export const searchChannelLocations = (
  channelId: string,
  query: string,
  language: "de" | "en",
): Promise<readonly PanelChannelLocationResult[]> => {
  const search = new URLSearchParams({ q: query, language });
  return requestJson<{ results: PanelChannelLocationResult[] }>(
    `${channelPath(channelId, "settings/location/geocode")}?${search}`,
  ).then(({ results }) => results);
};

export const saveChannelLocation = (
  channelId: string,
  revision: number,
  location: PanelChannelLocation | null,
): Promise<{ ok: boolean; location: PanelChannelLocation | null; locationRevision: number }> => requestMutation(
  channelPath(channelId, "settings/location"),
  "PATCH",
  { revision, location },
);

export const fetchSystemOverview = (
  channelId: string,
  signal?: AbortSignal,
): Promise<PanelSystemResponse> => requestJson<PanelSystemResponse>(
  channelPath(channelId, "system"),
  requestOptions(signal),
);

export const fetchAuditLog = (
  channelId: string,
  cursor: string | null = null,
  signal?: AbortSignal,
  filters?: PanelAuditFilters,
): Promise<PanelAuditResponse> => {
  const params = new URLSearchParams();
  if (cursor !== null) params.set("cursor", cursor);
  if (filters?.person !== null && filters?.person !== undefined) params.set("actor", filters.person);
  if (filters?.area !== null && filters?.area !== undefined) params.set("area", filters.area);
  const query = params.toString();
  return requestJson<PanelAuditResponse>(
    `${channelPath(channelId, "audit-log")}${query.length > 0 ? `?${query}` : ""}`,
    requestOptions(signal),
  );
};

const eventFilterParams = (filters?: PanelEventFilters): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters?.origin !== null && filters?.origin !== undefined) {
    params.set("origin", filters.origin);
  }
  if (filters?.module !== null && filters?.module !== undefined) params.set("module", filters.module);
  if (filters?.tones !== undefined && filters.tones.length > 1) {
    for (const tone of filters.tones) params.append("tone", tone);
  } else if (filters?.tone !== null && filters?.tone !== undefined) params.set("tone", filters.tone);
  if (filters?.person !== null && filters?.person !== undefined) params.set("actor", filters.person);
  return params;
};

export const fetchEvents = (
  channelId: string,
  cursor: string | null = null,
  signal?: AbortSignal,
  filters?: PanelEventFilters,
): Promise<PanelEventsResponse> => {
  const params = eventFilterParams(filters);
  if (cursor !== null) params.set("cursor", cursor);
  const query = params.toString();
  return requestJson<PanelEventsResponse>(
    `${channelPath(channelId, "events")}${query.length > 0 ? `?${query}` : ""}`,
    requestOptions(signal),
  );
};

export const fetchEventChanges = (
  channelId: string,
  after: number,
  signal?: AbortSignal,
  filters?: PanelEventFilters,
): Promise<PanelEventChangesResponse> => {
  const params = eventFilterParams(filters);
  params.set("after", String(after));
  params.set("limit", "100");
  return requestJson<PanelEventChangesResponse>(
    `${channelPath(channelId, "events")}?${params.toString()}`,
    requestOptions(signal),
  );
};

export const fetchMembers = (
  channelId: string,
  cursor: string | null = null,
  signal?: AbortSignal,
): Promise<PanelMembersResponse> => requestJson<PanelMembersResponse>(
  `${memberPath(channelId)}${cursor === null ? "" : `?${new URLSearchParams({ cursor }).toString()}`}`,
  requestOptions(signal),
);

export const fetchModules = (
  channelId: string,
  signal?: AbortSignal,
): Promise<PanelModulesResponse> => requestJson<PanelModulesResponse>(
  modulePath(channelId),
  requestOptions(signal),
);

export const searchTwitchUser = async (
  channelId: string,
  login: string,
  signal?: AbortSignal,
): Promise<{ user: PanelTwitchUser }> => {
  const params = new URLSearchParams({ login });
  return requestJson<{ user: PanelTwitchUser }>(
    `${memberPath(channelId)}/search?${params.toString()}`,
    requestOptions(signal),
  );
};

const requestMutation = <T>(
  path: string,
  method: "POST" | "PATCH" | "DELETE" | "PUT",
  body?: object,
  signal?: AbortSignal,
): Promise<T> => requestJson<{ token: string }>("/api/csrf", requestOptions(signal)).then(({ token }) => requestJson<T>(path, {
  method,
  headers: {
    ...(body === undefined ? {} : { "Content-Type": "application/json" }),
    "X-CSRF-Token": token,
  },
  ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  ...requestOptions(signal),
}));

export const addChannelMember = (
  channelId: string,
  userId: string,
  role: ChannelRole,
): Promise<{ member: PanelMember }> => requestMutation(
  memberPath(channelId),
  "POST",
  { userId, role },
);

export const updateChannelMemberRole = (
  channelId: string,
  userId: string,
  role: ChannelRole,
): Promise<{ member: PanelMember }> => requestMutation(
  memberPath(channelId, userId),
  "PATCH",
  { role },
);

export const removeChannelMember = (channelId: string, userId: string): Promise<undefined> =>
  requestMutation<undefined>(memberPath(channelId, userId), "DELETE");

export const setChannelModuleEnabled = (
  channelId: string,
  moduleId: string,
  enabled: boolean,
  expectedRevision?: number,
): Promise<{ module: PanelModuleState; revision: number }> => requestMutation(
  modulePath(channelId, moduleId),
  "PATCH",
  { enabled, ...(expectedRevision === undefined ? {} : { expectedRevision }) },
);

export const getChannelModuleSettings = (channelId: string, moduleId: string, signal?: AbortSignal): Promise<{
  settings: Record<string, unknown>;
  revision: number;
  variables: { name: string; value: number; description: string }[];
}> => requestJson<{ settings: Record<string, unknown>; revision: number; variables: { name: string; value: number; description: string }[] }>(
  `${modulePath(channelId, moduleId)}/settings`,
  requestOptions(signal),
);

export const getChannelModuleSummary = <Summary,>(channelId: string, moduleId: string, signal?: AbortSignal): Promise<Summary> =>
  requestJson<Summary>(`${modulePath(channelId, moduleId)}/summary`, requestOptions(signal));

export const getChannelTemplateVariables = async (
  channelId: string,
  signal?: AbortSignal,
): Promise<readonly ModuleRegisteredTemplateVariable[]> => {
  const response = await requestJson<{ variables: readonly ModuleRegisteredTemplateVariable[] }>(
    channelPath(channelId, "template-variables"),
    requestOptions(signal),
  );
  return response.variables;
};

export type PanelWeatherCurrent =
  | { status: "location_missing" }
  | { status: "provider_error"; reason: "upstream_unavailable" }
  | {
      status: "ok";
      location: { name: string; timeZone: string };
      weather: NormalizedWeather;
      expiresAt: string;
      showFahrenheit: boolean;
    };

export type PanelSunToday =
  | { status: "location_missing" }
  | { status: "ok"; day: SunDay; goldenHourIntervals: readonly SunAltitudeInterval[]; blueHourIntervals: readonly SunAltitudeInterval[]; nextDawnAt: string | null };

export type PanelMoonToday =
  | { status: "location_missing" }
  | {
      status: "ok";
      phase: string;
      illumination: number;
      riseAt: string | null;
      setAt: string | null;
      nextRiseAt: string | null;
      state: "normal" | "always_up" | "always_down" | "no_crossing";
    };

export const getChannelWeatherCurrent = (
  channelId: string,
  signal?: AbortSignal,
): Promise<PanelWeatherCurrent> => requestJson<PanelWeatherCurrent>(
  `${modulePath(channelId, "weather")}/current`,
  requestOptions(signal),
);

export const getChannelSunToday = (
  channelId: string,
  signal?: AbortSignal,
): Promise<PanelSunToday> => requestJson<PanelSunToday>(
  `${modulePath(channelId, "sun")}/today`,
  requestOptions(signal),
);

export const getChannelMoonToday = (
  channelId: string,
  signal?: AbortSignal,
): Promise<PanelMoonToday> => requestJson<PanelMoonToday>(
  `${modulePath(channelId, "moon")}/today`,
  requestOptions(signal),
);

export interface PanelChannelVariableRecord {
  channelId: string;
  name: string;
  value: number;
  description: string;
  resetOnStreamStart: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PanelChannelVariableUsage {
  moduleId: string;
  itemName: string;
  kind: "template" | "action" | "display";
  overlayId?: string;
  elementId?: string;
  elementLabel?: string;
  reconnect?: boolean;
}

export interface PanelChannelVariable extends PanelChannelVariableRecord {
  usages: readonly PanelChannelVariableUsage[];
}

export interface PanelChannelVariablesResponse {
  variables: readonly PanelChannelVariable[];
  count: number;
  maximum: number;
}

export interface PanelChannelVariableAvailabilityResponse {
  available: boolean;
}

const variablesPath = (channelId: string, name?: string, suffix = ""): string =>
  `${channelPath(channelId, "variables")}${name === undefined ? "" : `/${encodeURIComponent(name)}`}${suffix}`;

export const fetchChannelVariables = (channelId: string, signal?: AbortSignal): Promise<PanelChannelVariablesResponse> =>
  requestJson<PanelChannelVariablesResponse>(variablesPath(channelId), signal === undefined ? {} : { signal });

export const fetchChannelVariableAvailability = (channelId: string, signal?: AbortSignal): Promise<PanelChannelVariableAvailabilityResponse> =>
  requestJson<PanelChannelVariableAvailabilityResponse>(`${variablesPath(channelId)}/availability`, requestOptions(signal));

export const createChannelVariable = (
  channelId: string,
  variable: Pick<PanelChannelVariableRecord, "name" | "value" | "description" | "resetOnStreamStart">,
): Promise<{ variable: PanelChannelVariableRecord; usages: readonly PanelChannelVariableUsage[] }> =>
  requestMutation(variablesPath(channelId), "POST", {
    name: variable.name,
    value: variable.value,
    description: variable.description,
    resetOnStreamStart: variable.resetOnStreamStart,
  });

export const updateChannelVariable = (
  channelId: string,
  name: string,
  update: { newName: string; description: string; resetOnStreamStart: boolean },
): Promise<{ variable: PanelChannelVariableRecord; usages: readonly PanelChannelVariableUsage[] }> =>
  requestMutation(variablesPath(channelId, name), "PATCH", update);

export const changeChannelVariableValue = (
  channelId: string,
  name: string,
  operation: "add" | "subtract" | "set",
  amount: number,
): Promise<{ variable: PanelChannelVariableRecord }> =>
  requestMutation(variablesPath(channelId, name, "/value"), "POST", { operation, amount });

export const sendTestMessage = (channelId: string): Promise<TestMessageResult> =>
  requestMutation(channelPath(channelId, "quick-actions/test-message"), "POST");

export const deleteChannelVariable = (channelId: string, name: string): Promise<undefined> =>
  requestMutation<undefined>(variablesPath(channelId, name), "DELETE");

export const saveChannelModuleSettings = <Settings extends object>(
  channelId: string,
  moduleId: string,
  revision: number,
  settings: Settings,
  belaboxMutation?: { statsUrl?: string },
): Promise<{ settings: Settings; revision: number; warnings: PanelTemplateWarningResponse["warnings"] }> => requestJson<{ token: string }>("/api/csrf")
  .then(({ token }) => requestJson<{ settings: Settings; revision: number; warnings: PanelTemplateWarningResponse["warnings"] }>(`${modulePath(channelId, moduleId)}/settings`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
    body: JSON.stringify({ revision, settings, ...belaboxMutation }),
  }));

export const refreshModeratorStatus = (
  channelId: string,
): Promise<{ moderator: PanelModeratorStatus; nextAllowedAt: string }> => requestMutation(
  channelPath(channelId, "moderator-status"),
  "POST",
);

export const startCommercial = (
  channelId: string,
  length: number,
): Promise<{ length: number | null; message: string | null; retryAfter: number | null }> => requestMutation(
  `${modulePath(channelId, "ads")}/commercial`,
  "POST",
  { length },
);

export const sendManualShoutout = (
  channelId: string,
  login: string,
): Promise<{ sent: true }> => requestMutation(
  channelPath(channelId, "shoutout"),
  "POST",
  { login },
);

export const createClip = (
  channelId: string,
): Promise<{ clipId: string | null; editUrl: string | null }> => requestMutation(
  channelPath(channelId, "clips"),
  "POST",
);

export const setChannelControl = (
  channelId: string,
  control: "mute" | "pause",
  duration: ChannelControlDuration | null,
): Promise<{ controls: PanelChannelControls }> => requestMutation(
  channelPath(channelId, `controls/${control}`),
  "POST",
  { duration },
);

export const logout = async (): Promise<void> => {
  const csrf = await requestJson<{ token: string }>("/api/csrf");
  await requestJson<undefined>("/auth/logout", {
    method: "POST",
    headers: { "X-CSRF-Token": csrf.token },
  });
};
